/**
 * Web Storefront Auth Store — Supabase Auth
 * Replaces Firebase Auth for customer login/session management.
 */

import { create } from 'zustand';
import { API_ORIGIN } from '../lib/apiBase';
import { createClient, type Session, type User } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

function getSupabase() {
  return createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: { autoRefreshToken: true, persistSession: true, detectSessionInUrl: true },
  });
}

const API_BASE = API_ORIGIN;

interface AuthState {
  session: Session | null;
  supabaseUser: User | null;
  userProfile: Record<string, unknown> | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  initialize: () => () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  fetchUserProfile: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  session: null,
  supabaseUser: null,
  userProfile: null,
  isLoading: true,
  isInitialized: false,
  error: null,

  initialize: () => {
    const supabase = getSupabase();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      set({
        session,
        supabaseUser: session?.user ?? null,
        isInitialized: true,
        isLoading: false,
      });
      if (session) {
        await get().fetchUserProfile();
      } else {
        set({ userProfile: null });
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        set({ isLoading: false, isInitialized: true });
      }
    });

    return () => subscription.unsubscribe();
  },

  signIn: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      set({ session: data.session, supabaseUser: data.user, isLoading: false });
      await get().fetchUserProfile();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  signUp: async (email, password, name) => {
    set({ isLoading: true, error: null });
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { display_name: name } },
      });
      if (error) throw error;

      // Register user in our API (creates DB profile)
      if (data.session) {
        await fetch(`${API_BASE}/api/auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${data.session.access_token}`,
          },
          body: JSON.stringify({ email, displayName: name, password }),
        });
      }

      set({ session: data.session, supabaseUser: data.user, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign up failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  signInWithGoogle: async () => {
    set({ isLoading: true, error: null });
    try {
      const supabase = getSupabase();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) throw error;
      set({ isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Google sign in failed';
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  signOut: async () => {
    const supabase = getSupabase();
    await supabase.auth.signOut();
    set({ session: null, supabaseUser: null, userProfile: null, isLoading: false });
  },

  resetPassword: async (email) => {
    set({ error: null });
    try {
      const supabase = getSupabase();
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Password reset failed';
      set({ error: message });
      throw err;
    }
  },

  fetchUserProfile: async () => {
    try {
      const { session } = get();
      if (!session) return;

      const response = await fetch(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (response.ok) {
        const data = await response.json();
        set({ userProfile: data.data });
      }
    } catch { /* silently fail */ }
  },

  clearError: () => set({ error: null }),
}));
